import React, { useState, useEffect, useCallback, useRef } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import LeftMenu from "../components/LeftMenu";
import LeftShadow from "../components/LeftShadow";
import MediaBrowse from "../components/media-browse/MediaBrowse";
import RottenTomatoesRow from "../components/media-browse/RottenTomatoesRow";
import MobileMenu from "../components/MobileMenu";
import useFontSize from "../hooks/useFontSize";
import apiService from "../services/api";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerMoviesLibrary = () => {
  useFontSize(); // Apply font size settings
  const navigate = useNavigate();
  const location = useLocation();
  const loadWatchedRef = useRef(null);
  const loadCollectionsRef = useRef(null);

  const profile =
    location.state?.profile ||
    JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist =
    location.state?.playlist ||
    JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");
  const initialProfileRef = useRef(profile);

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const routeMode = location.pathname.startsWith(
    "/web-player/movies/collections"
  )
    ? "collections"
    : "watched";
  const initialModeRef = useRef(routeMode);
  const [viewMode, setViewMode] = useState(routeMode);
  const [watchedMovies, setWatchedMovies] = useState([]);
  const [collections, setCollections] = useState([]);
  const [isLoadingWatched, setIsLoadingWatched] = useState(false);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [collectionsHasMore, setCollectionsHasMore] = useState(true);
  const collectionsObserverRef = useRef(null);
  const collectionsLoadingRef = useRef(false);
  const searchInputRef = useRef(null);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [collectionMovies, setCollectionMovies] = useState([]);
  const [isLoadingCollectionDetail, setIsLoadingCollectionDetail] =
    useState(false);

  useEffect(() => {
    const initialProfile = initialProfileRef.current;

    if (initialProfile?.id) {
      sessionStorage.setItem("selectedProfile", JSON.stringify(initialProfile));
    }

    if (initialModeRef.current === "watched") loadWatchedRef.current?.();
    else loadCollectionsRef.current?.(1, true);
  }, []);

  const clearSearch = () => {
    setSearchQuery("");
    setShowSearch(false);
  };

  // Filter watched movies by search query (local filtering)
  const filteredWatchedMovies = searchQuery
    ? watchedMovies.filter((movie) =>
        (movie.title || movie.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : watchedMovies;

  // Filter collections by search query (local filtering)
  const filteredCollections = searchQuery
    ? collections.filter((collection) =>
        (collection.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : collections;

  // Load watched movies
  const loadWatchedMovies = async () => {
    setIsLoadingWatched(true);
    try {
      const response = await apiService.webPlayerRequest(
        "/v2/recently-watched/watched?mediaType=movie&limit=100"
      );

      if (response?.data) {
        const moviesData = Array.isArray(response.data) ? response.data : [];
        const mappedMovies = moviesData.map((item) => ({
          id: item.mediaId && item.mediaId !== -1 ? item.mediaId : item.id,
          mediaId: item.mediaId && item.mediaId !== -1 ? item.mediaId : item.id,
          title: item.name || item.title,
          name: item.name || item.title,
          poster: item.posterPath || item.poster,
          posterPath: item.posterPath || item.poster,
          backdropPath: item.backdropPath,
          year: item.year,
          rating: item.rating,
          genres: item.genres,
          rottenTomatoes: item.rottenTomatoes,
        }));

        setWatchedMovies(mappedMovies);
      }
    } catch (error) {
      console.error("Failed to load watched movies:", error);
      toast.error("Failed to load watched movies");
    } finally {
      setIsLoadingWatched(false);
    }
  };

  // Load collections with pagination
  const loadCollections = async (pageNum = 1, reset = false) => {
    if (collectionsLoadingRef.current && !reset) return;
    collectionsLoadingRef.current = true;

    setIsLoadingCollections(true);
    try {
      const response = await apiService.webPlayerRequest(
        `/v2/collections?page=${pageNum}&limit=70`
      );

      if (response?.data) {
        const collectionsData = Array.isArray(response.data)
          ? response.data
          : [];

        if (reset) {
          setCollections(collectionsData);
          setCollectionsPage(pageNum);
        } else {
          setCollections((prev) => [...prev, ...collectionsData]);
          setCollectionsPage(pageNum);
        }

        // Check if there are more pages
        const totalPages = response?.meta?.totalPages || 1;

        setCollectionsHasMore(pageNum < totalPages);
      }
    } catch (error) {
      console.error("Failed to load collections:", error);
      toast.error("Failed to load collections");
    } finally {
      setIsLoadingCollections(false);
      collectionsLoadingRef.current = false;
    }
  };

  loadWatchedRef.current = loadWatchedMovies;
  loadCollectionsRef.current = loadCollections;

  // Intersection observer for collections lazy loading
  const lastCollectionElementRef = useCallback(
    (node) => {
      if (isLoadingCollections) return;
      if (collectionsObserverRef.current)
        collectionsObserverRef.current.disconnect();

      collectionsObserverRef.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          collectionsHasMore &&
          !collectionsLoadingRef.current
        ) {
          loadCollections(collectionsPage + 1, false);
        }
      });

      if (node) collectionsObserverRef.current.observe(node);
    },
    [isLoadingCollections, collectionsHasMore, collectionsPage]
  );

  // Load collection detail
  const loadCollectionDetail = async (collectionId) => {
    setIsLoadingCollectionDetail(true);
    try {
      const response = await apiService.webPlayerRequest(
        `/v2/collections/${collectionId}`
      );

      if (response?.data) {
        const collection = response.data;
        // Get movies from parts or availableMovies
        const parts = collection.parts || [];
        const availableMovies = collection.availableMovies || [];

        const moviesData = parts.length > 0 ? parts : availableMovies;
        const mappedMovies = moviesData.map((item) => ({
          id: item.mediaId && item.mediaId !== -1 ? item.mediaId : item.id,
          mediaId: item.mediaId && item.mediaId !== -1 ? item.mediaId : item.id,
          title: item.name || item.title,
          name: item.name || item.title,
          poster: item.posterPath || item.poster,
          posterPath: item.posterPath || item.poster,
          backdropPath: item.backdropPath,
          year: item.year,
          rating: item.rating,
          genres: item.genres,
          tmdbId: item.tmdbId,
          isBookmarked: item.isBookmarked,
          rottenTomatoes: item.rottenTomatoes,
        }));

        setCollectionMovies(mappedMovies);
        setCurrentCollection({
          ...collection,
          movieCount: mappedMovies.length,
        });
      }
    } catch (error) {
      console.error("Failed to load collection detail:", error);
      toast.error("Failed to load collection");
    } finally {
      setIsLoadingCollectionDetail(false);
    }
  };

  // Handle collection click
  const handleCollectionClick = (collection) => {
    setCurrentCollection(collection);
    setViewMode("collection-detail");
    loadCollectionDetail(collection.id);
  };

  // Filter collection movies by search query (local filtering)
  const filteredCollectionMovies = searchQuery
    ? collectionMovies.filter((movie) =>
        (movie.title || movie.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : collectionMovies;

  const isDetail = viewMode === "collection-detail";
  const isCollections = viewMode === "collections";
  const libraryTitle =
    viewMode === "watched"
      ? "Watched"
      : isDetail
        ? currentCollection?.name || "Collection"
        : "Collections";
  const libraryTotal =
    viewMode === "watched"
      ? watchedMovies.length
      : isDetail
        ? collectionMovies.length
        : collections.length;
  const libraryShown =
    viewMode === "watched"
      ? filteredWatchedMovies.length
      : isDetail
        ? filteredCollectionMovies.length
        : filteredCollections.length;
  const libraryLoading =
    viewMode === "watched"
      ? isLoadingWatched
      : isDetail
        ? isLoadingCollectionDetail
        : isLoadingCollections;
  const posterItems = isDetail
    ? filteredCollectionMovies
    : filteredWatchedMovies;
  const emptyCopy = searchQuery
    ? {
        icon: "fa-search",
        message: `No results for "${searchQuery}"`,
        sub: "Try a different search term",
      }
    : viewMode === "watched"
      ? {
          icon: "fa-eye",
          message: "No watched movies yet",
          sub: "Movies you've watched will appear here",
        }
      : isDetail
        ? {
            icon: "fa-film-stack",
            message: "No movies in this collection",
            sub: "Movies from this collection will appear here",
          }
        : {
            icon: "fa-film-stack",
            message: "No collections found",
            sub: "Movie collections will appear here",
          };

  const openLibraryMovie = (item) => {
    const mediaId =
      item.mediaId && item.mediaId !== -1
        ? item.mediaId
        : item.tmdbId || item.id;

    if (mediaId && mediaId !== -1) navigate(`/web-player/movie/${mediaId}`);
  };

  const closeLibrary = () => {
    setSearchQuery("");
    setShowSearch(false);
    if (isDetail) setViewMode("collections");
    else navigate("/web-player/movies");
  };

  return (
    <div>
      <MobileMenu />
      <button
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          webPlayerStorage.clearAll();
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>
      <LeftMenu activeMenuItem="movies" playlist={playlist} profile={profile} />

      <section className="media-browse">
        {isDetail && currentCollection?.backdropPath && (
          <img
            alt=""
            aria-hidden="true"
            className="media-browse-backdrop"
            src={currentCollection.backdropPath}
          />
        )}
        <LeftShadow className="fixed inset-0 z-0" />

        <header className="media-browse-header library-header">
          <h1 className="media-browse-title">
            <span className="library-title-text">{libraryTitle}</span>
            {libraryTotal > 0 && (
              <span className="media-browse-count">
                ({searchQuery ? `${libraryShown}/` : ""}
                {libraryTotal})
              </span>
            )}
          </h1>

          {showSearch ? (
            <label className="glass-capsule library-search">
              <i className="fas fa-search" />
              <input
                autoFocus
                aria-label="Search"
                placeholder={
                  isCollections ? "Search collections" : "Search movies"
                }
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") clearSearch();
                }}
              />
              <button
                aria-label="Clear search"
                type="button"
                onClick={clearSearch}
              >
                <i className="fas fa-times" />
              </button>
            </label>
          ) : (
            <button
              className="glass-capsule library-search"
              type="button"
              onClick={() => setShowSearch(true)}
            >
              <i className="fas fa-search" />
              Search
            </button>
          )}

          <button
            aria-label={isDetail ? "Back to collections" : "Back to movies"}
            className="glass-capsule is-icon library-close"
            type="button"
            onClick={closeLibrary}
          >
            <i className={`fas ${isDetail ? "fa-chevron-left" : "fa-times"}`} />
          </button>
        </header>

        <div className="media-browse-stage">
          {libraryLoading && libraryTotal === 0 && (
            <div aria-hidden="true" className="media-grid">
              {Array.from({ length: 14 }, (_, index) => (
                <div className="media-shimmer poster" key={index} />
              ))}
            </div>
          )}

          {isCollections && libraryShown > 0 && (
            <div className="media-grid">
              {filteredCollections.map((collection, index) => (
                <button
                  className="collection-card"
                  key={collection.id}
                  ref={
                    index === filteredCollections.length - 1
                      ? lastCollectionElementRef
                      : null
                  }
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setShowSearch(false);
                    handleCollectionClick(collection);
                  }}
                >
                  <span className="poster-card lunova-card">
                    <LibraryPoster
                      alt={collection.name}
                      icon="fa-film-stack"
                      src={collection.posterPath || collection.backdropPath}
                    />
                    {collection.availableCount > 0 && (
                      <span className="collection-count">
                        {collection.availableCount}
                      </span>
                    )}
                  </span>
                  <span className="collection-title">{collection.name}</span>
                </button>
              ))}
            </div>
          )}

          {!isCollections && libraryShown > 0 && (
            <div className="media-grid">
              {posterItems.map((item, index) => (
                <div
                  className="media-card lunova-card-host"
                  key={item.id || index}
                >
                  <button
                    className="poster-card lunova-card"
                    type="button"
                    onClick={() => openLibraryMovie(item)}
                  >
                    <LibraryPoster
                      alt={item.title || item.name}
                      icon="fa-film"
                      src={item.poster || item.posterPath}
                    />
                  </button>
                  <RottenTomatoesRow ratings={item.rottenTomatoes} />
                </div>
              ))}
            </div>
          )}

          {!libraryLoading && libraryShown === 0 && (
            <div className="browse-empty">
              <i className={`fa-solid ${emptyCopy.icon}`} />
              <p>{emptyCopy.message}</p>
              <span>{emptyCopy.sub}</span>
            </div>
          )}

          {libraryLoading && libraryTotal > 0 && (
            <div className="browse-status">
              <span>Loading...</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const LibraryPoster = ({ src, alt, icon }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span className="poster-fallback">
        <i className={`fa-solid ${icon}`} />
      </span>
    );
  }

  return (
    <img
      alt={alt}
      draggable={false}
      src={src}
      onError={() => setFailed(true)}
    />
  );
};

const WebPlayerMovies = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const profile =
    location.state?.profile ||
    JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist =
    location.state?.playlist ||
    JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");
  const libraryRoute =
    location.pathname.startsWith("/web-player/movies/watched") ||
    location.pathname.startsWith("/web-player/movies/collections");

  if (libraryRoute) return <WebPlayerMoviesLibrary key={location.pathname} />;

  return (
    <div>
      <MobileMenu />
      <button
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          webPlayerStorage.clearAll();
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>
      <LeftMenu activeMenuItem="movies" playlist={playlist} profile={profile} />
      <MediaBrowse
        mediaType="movies"
        playlist={playlist}
        profile={profile}
        onOpenCollections={() => navigate("/web-player/movies/collections")}
        onOpenWatched={() => navigate("/web-player/movies/watched")}
      />
    </div>
  );
};

export default WebPlayerMovies;
